import React, { useState, useEffect } from 'react';
import { BarChart3, Layers, TrendingUp, Activity, Info } from 'lucide-react';
import { API_ENDPOINTS, fetchWithRetry } from '../config/api';

interface Pool {
  poolAddress: string;
  dex: string;
  price: number;
  alphReserve: number;
  tokenReserve: number;
  liquidity: number;
  confidence: number;
  lastUpdated: number;
}

interface PoolData {
  symbol: string;
  pools: Pool[];
  aggregatedPrice: number;
  totalLiquidity: number;
  weightedPrice: number;
  timestamp: number;
}

interface TokenPoolDetailsProps {
  symbol: string;
  onClose: () => void;
}

export const TokenPoolDetails: React.FC<TokenPoolDetailsProps> = ({ symbol, onClose }) => {
  const [poolData, setPoolData] = useState<PoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPoolData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchWithRetry(API_ENDPOINTS.pools(symbol));
        if (!response.ok) {
          throw new Error(`Failed to fetch pool data: ${response.statusText}`);
        }
        
        const data = await response.json();
        setPoolData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPoolData();
  }, [symbol]);

  const formatPrice = (price: number) => {
    if (price < 0.001) return price.toFixed(8);
    if (price < 1) return price.toFixed(6);
    if (price < 100) return price.toFixed(4);
    return price.toFixed(2);
  };

  const formatLiquidity = (liquidity: number) => {
    if (liquidity >= 1000000) return `${(liquidity / 1000000).toFixed(2)}M`;
    if (liquidity >= 1000) return `${(liquidity / 1000).toFixed(2)}K`;
    return liquidity.toFixed(2);
  };

  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.7) return 'text-green-600 bg-green-100';
    if (confidence >= 0.5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const isProblematicPool = (pool: Pool) => {
    // Detect pools that might be problematic for weighted calculations
    return (
      pool.alphReserve < 10 || // Very low liquidity
      pool.confidence < 0.3 || // Very low confidence
      pool.price > 1000000 || // Extremely high price (likely an error)
      pool.tokenReserve === 0 // No token reserves
    );
  };

  const getPoolStatus = (pool: Pool) => {
    if (isProblematicPool(pool)) {
      return { 
        status: 'problematic', 
        class: 'bg-red-50 border-red-200',
        badge: 'bg-red-100 text-red-700',
        text: 'Excluded from weighting'
      };
    }
    return { 
      status: 'normal', 
      class: 'bg-white border-gray-200',
      badge: 'bg-green-100 text-green-700',
      text: 'Included in weighting'
    };
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Loading {symbol} Pool Details...</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-red-600">Error</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={onClose}
            className="mt-4 w-full bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!poolData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Layers className="w-6 h-6 text-blue-500" />
            {symbol} Pool Details
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
            ✕
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="card bg-blue-50">
            <div className="card-body text-center">
              <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Aggregated Price</p>
              <p className="text-lg font-semibold">{formatPrice(poolData.aggregatedPrice)} ALPH</p>
            </div>
          </div>
          
          <div className="card bg-green-50">
            <div className="card-body text-center">
              <BarChart3 className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Weighted Price</p>
              <p className="text-lg font-semibold">{formatPrice(poolData.weightedPrice)} ALPH</p>
            </div>
          </div>
          
          <div className="card bg-purple-50">
            <div className="card-body text-center">
              <Activity className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Total Liquidity</p>
              <p className="text-lg font-semibold">{formatLiquidity(poolData.totalLiquidity)} ALPH</p>
            </div>
          </div>
          
          <div className="card bg-orange-50">
            <div className="card-body text-center">
              <Layers className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Pool Count</p>
              <p className="text-lg font-semibold">{poolData.pools.length}</p>
            </div>
          </div>
        </div>

        {/* Pool List */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Info className="w-5 h-5 text-gray-500" />
            Individual Pools (sorted by confidence)
          </h3>
          
          {poolData.pools
            .sort((a, b) => b.confidence - a.confidence) // Ensure highest confidence first
            .map((pool, index) => {
              const poolStatus = getPoolStatus(pool);
              
              return (
                <div key={pool.poolAddress} className={`card hover:shadow-md transition-shadow border-2 ${poolStatus.class}`}>
                  <div className="card-body">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      
                      {/* Pool Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-semibold">#{index + 1}</span>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                            {pool.dex}
                          </span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getConfidenceColor(pool.confidence)}`}>
                            {(pool.confidence * 100).toFixed(0)}% confidence
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${poolStatus.badge}`}>
                            {poolStatus.text}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Pool:</span> {formatAddress(pool.poolAddress)}
                        </p>
                        
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Last Updated:</span> {formatTimestamp(pool.lastUpdated)}
                        </p>
                      </div>

                      {/* Price & Reserves */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-600">Price</p>
                          <p className="font-semibold text-lg">{formatPrice(pool.price)}</p>
                          <p className="text-xs text-gray-500">ALPH</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">ALPH Reserve</p>
                          <p className="font-semibold">{formatLiquidity(pool.alphReserve)}</p>
                          <p className="text-xs text-gray-500">ALPH</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">{symbol} Reserve</p>
                          <p className="font-semibold">{formatLiquidity(pool.tokenReserve)}</p>
                          <p className="text-xs text-gray-500">{symbol}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">TVL</p>
                          <p className="font-semibold">{formatLiquidity(pool.liquidity)}</p>
                          <p className="text-xs text-gray-500">ALPH</p>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <button
            onClick={onClose}
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
